import React, { useEffect } from "react";
import {
  CircularProgress,
  Grid2 as Grid,
  Stack,
  Typography,
} from "@mui/material";
import { useFieldArray } from "react-hook-form";

import { Form, Modal } from "@shared/components";
import { useCloneAgreementModal } from "./controller";
import { CloneAgreementModalProps } from "./types";

export const CloneAgreementModal = React.memo(
  ({
    modalRef,
    agreementId,
    sourceName,
    autoOpen = false,
    onClose,
  }: CloneAgreementModalProps) => {
    const {
      methods,
      isPending,
      isLoadingSlugs,
      handleConfirm,
      loadSourceSlugs,
      translate,
    } = useCloneAgreementModal({
      modalRef,
      agreementId,
      sourceName,
    });

    const { fields } = useFieldArray({
      control: methods.control,
      name: "slugEntries",
    });

    useEffect(() => {
      if (autoOpen) {
        modalRef.current?.open();
      }
    }, [autoOpen, modalRef]);

    return (
      <Modal
        ref={modalRef}
        title={translate("v1/agreement_admin.messages.clone.title")}
        confirmLabel={translate("v1/agreement_admin.messages.clone.confirm")}
        methods={methods}
        isPending={isPending || isLoadingSlugs}
        onConfirm={handleConfirm as any}
        onOpen={loadSourceSlugs}
        onClose={onClose}
      >
        <Grid container spacing={2}>
          <Grid size={{ xs: 12 }}>
            <Typography variant="body2" color="text.secondary">
              {translate("v1/agreement_admin.messages.clone.description")}
            </Typography>
            <Typography
              variant="caption"
              display="block"
              sx={{ mt: 0.75, color: "text.disabled", fontStyle: "italic" }}
            >
              {translate("v1/agreement_admin.messages.clone.note")}
            </Typography>
          </Grid>

          <Grid size={{ xs: 12 }}>
            <Form.Input
              name="name"
              label={translate("v1/agreement_admin.messages.clone.new_name")}
              required
            />
          </Grid>

          <Grid size={{ xs: 12 }}>
            <Typography variant="subtitle2" color="primary.main">
              {translate("v1/agreement_admin.messages.clone.slug_map_title")}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              {translate("v1/agreement_admin.messages.clone.slug_map_hint")}
            </Typography>
          </Grid>

          {isLoadingSlugs ? (
            <Grid size={{ xs: 12 }}>
              <Stack alignItems="center" py={2}>
                <CircularProgress size={32} />
              </Stack>
            </Grid>
          ) : fields.length === 0 ? (
            <Grid size={{ xs: 12 }}>
              <Typography variant="body2" color="error">
                {translate("v1/agreement_admin.messages.clone.no_slugs")}
              </Typography>
            </Grid>
          ) : (
            fields.map((field, index) => (
              <Grid size={{ xs: 12 }} key={field.id}>
                <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
                  <Form.Input
                    name={`slugEntries.${index}.old_slug`}
                    label={translate(
                      "v1/agreement_admin.messages.clone.old_slug"
                    )}
                    disabled
                  />
                  <Form.Input
                    name={`slugEntries.${index}.new_slug`}
                    label={translate(
                      "v1/agreement_admin.messages.clone.new_slug"
                    )}
                    required
                  />
                </Stack>
              </Grid>
            ))
          )}
        </Grid>
      </Modal>
    );
  }
);
